import { API_BASE_URL, API_KEY } from '../../../api/auth/authApi'
import type { FleetRecord } from '../../types'
import { ADMIN_TOKEN_KEY } from '../../auth/adminAuth'

export type FleetApiResponse<T = unknown> = {
  success: boolean
  message: string
  data?: T
  warning?: string
}

export type FleetPayload = {
  name: string
  categoryId: number
  brandId: number
  registration: string
  passengers: number
  suitcases: number
  cabinBags: number
  features: string[]
  description: string
  basePrice: number
  dayTripPrice: number
  hourlyPrice: number
  pricePerKm: number
  minimumBookingHours: number
  displayOrder: number
  active: boolean
  imageFile: File | null
}

type FleetApiRecord = FleetPayload & {
  id: number | string
  category?: string
  brand?: string
  registration?: string
  license_plate?: string
  passengers?: number
  max_passengers?: number
  suitcases?: number
  max_suitcases?: number
  max_cabin_bags?: number
  active?: boolean
  is_active?: boolean
  image?: string
  vehicle_type?: string

  categoryId?: number | null
  category_id?: number | null

  brandId?: number | null
  brand_id?: number | null

  cabinBags?: number

  basePrice?: number
  base_price?: number

  dayTripPrice?: number
  day_trip_price?: number

  hourlyPrice?: number
  hourly_price?: number

  pricePerKm?: number
  price_per_km?: number

  minimumBookingHours?: number
  minimum_booking_hours?: number

  displayOrder?: number
  display_order?: number
}

const API_PATH_URL = `${API_BASE_URL}/api/admin`

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    if (['true', '1', 'yes', 'active'].includes(normalized)) return true
    if (['false', '0', 'no', 'inactive'].includes(normalized)) return false
  }

  return undefined
}

const getHeaders = (): HeadersInit => {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY)

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  }
}

const buildFleetFormData = (payload: FleetPayload): FormData => {
  const formData = new FormData()

  formData.append('name', payload.name)
  formData.append('category', String(payload.categoryId))
  formData.append('brand', String(payload.brandId))
  formData.append('license_plate', payload.registration)
  formData.append('max_passengers', String(payload.passengers))
  formData.append('max_suitcases', String(payload.suitcases))
  formData.append('max_cabin_bags', String(payload.cabinBags))
  formData.append('features', JSON.stringify(payload.features))
  formData.append('description', payload.description)

  // Pricing
  formData.append('base_price', String(payload.basePrice))
  formData.append('day_trip_price', String(payload.dayTripPrice))
  formData.append('hourly_price', String(payload.hourlyPrice))
  formData.append('price_per_km', String(payload.pricePerKm))

  // Booking settings
  formData.append(
    'minimum_booking_hours',
    String(payload.minimumBookingHours),
  )

  // Fleet display ordering
  formData.append('display_order', String(payload.displayOrder))

  formData.append('is_active', String(payload.active))

  if (payload.imageFile) {
    formData.append('image', payload.imageFile)
  }

  return formData
}

const getErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const result = await response.json().catch(() => null)

  if (result?.detail) return result.detail
  if (result?.message) return result.message

  if (typeof result === 'object' && result !== null) {
    const firstError = Object.values(result)[0]

    if (Array.isArray(firstError) && firstError.length > 0) {
      return String(firstError[0])
    }
  }

  return fallback
}

const mapFleet = (fleet: FleetApiRecord): FleetRecord => ({
  id: String(fleet.id),

  category: fleet.category || fleet.vehicle_type || '',
  categoryId: fleet.categoryId ?? fleet.category_id ?? null,

  name: fleet.name,

  brandId: fleet.brandId ?? fleet.brand_id ?? null,

  plate: fleet.registration ?? fleet.license_plate ?? '',

  image: fleet.image || '',

  passengers: fleet.passengers ?? fleet.max_passengers ?? 0,

  luggage: fleet.suitcases ?? fleet.max_suitcases ?? 0,

  cabinBags: fleet.cabinBags ?? fleet.max_cabin_bags ?? 0,

  features: fleet.features || [],

  description: fleet.description || '',

  // Pricing
  basePrice: fleet.basePrice ?? fleet.base_price ?? 0,

  dayTripPrice:
    fleet.dayTripPrice ?? fleet.day_trip_price ?? 0,

  hourlyPrice:
    fleet.hourlyPrice ?? fleet.hourly_price ?? 0,

  pricePerKm:
    fleet.pricePerKm ?? fleet.price_per_km ?? 0,

  // Booking settings
  minimumBookingHours:
    fleet.minimumBookingHours ??
    fleet.minimum_booking_hours ??
    1,

  // Display ordering
  displayOrder:
    fleet.displayOrder ??
    fleet.display_order ??
    0,

  active:
    parseBoolean(fleet.active) ??
    parseBoolean(fleet.is_active) ??
    false,
})

export async function apiGetFleets(): Promise<
  FleetApiResponse<FleetRecord[]>
> {
  try {
    const response = await fetch(`${API_PATH_URL}/fleets/`, {
      method: 'GET',
      headers: getHeaders(),
    })

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to load fleets.',
        ),
      }
    }

    const result = await response.json()

    const data = Array.isArray(result)
      ? result
      : result?.data || result?.results || []

    return {
      success: true,
      message: result?.message || 'Fleets loaded successfully.',
      data: data.map(mapFleet),
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

export async function apiGetFleet(
  id: string,
): Promise<FleetApiResponse<FleetRecord>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/fleets/${id}/`,
      {
        method: 'GET',
        headers: getHeaders(),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to load fleet.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message: result?.message || 'Fleet loaded successfully.',
      data: mapFleet(result?.data || result),
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

export async function apiToggleFleetStatus(
  id: string,
  active: boolean,
): Promise<FleetApiResponse<FleetRecord>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/fleets/${id}/`,
      {
        method: 'PATCH',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: active,
        }),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to toggle fleet status.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message:
        result?.message ||
        'Fleet status updated successfully.',
      data: mapFleet(result?.data || result),
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

export async function apiCreateFleet(
  payload: FleetPayload,
): Promise<FleetApiResponse<FleetRecord>> {
  try {
    const formData = buildFleetFormData(payload)

    console.log('Fleet POST FormData:', {
      name: payload.name,
      category: payload.categoryId,
      brand: payload.brandId,
      license_plate: payload.registration,
      base_price: payload.basePrice,
      day_trip_price: payload.dayTripPrice,
      hourly_price: payload.hourlyPrice,
      price_per_km: payload.pricePerKm,
      minimum_booking_hours: payload.minimumBookingHours,
      display_order: payload.displayOrder,
      image: payload.imageFile?.name || null,
    })

    const response = await fetch(
      `${API_PATH_URL}/fleets/`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: formData,
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to create fleet.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message:
        result?.message ||
        'Fleet created successfully.',
      data: mapFleet(result?.data || result),
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}

export async function apiUpdateFleet(
  id: string,
  payload: FleetPayload,
): Promise<FleetApiResponse<FleetRecord>> {
  try {
    const response = await fetch(
      `${API_PATH_URL}/fleets/${id}/`,
      {
        method: 'PATCH',
        headers: getHeaders(),
        body: buildFleetFormData(payload),
      },
    )

    if (!response.ok) {
      return {
        success: false,
        message: await getErrorMessage(
          response,
          'Failed to update fleet.',
        ),
      }
    }

    const result = await response.json()

    return {
      success: true,
      message:
        result?.message ||
        'Fleet updated successfully.',
      data: mapFleet(result?.data || result),
    }
  } catch (error) {
    return {
      success: false,
      message: 'Connection failed.',
      warning: `Unable to reach backend API at ${API_BASE_URL}.`,
    }
  }
}